import { Link } from 'react-router-dom';
import { pick } from '../utils/privateLayouts';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Plus, Edit, Trash2, Copy, Check, X } from 'lucide-react';
import BackButton from '../components/BackButton';

export default function DesignPresetsSettings() {
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/design-presets', {
        withCredentials: true,
      });
      setPresets(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load text styles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedPreset(null);
    setShowEditor(true);
  };

  const handleEdit = (preset) => {
    setSelectedPreset(preset);
    setShowEditor(true);
  };

  const handleDelete = async (presetId) => {
    if (!window.confirm('Delete this text style? Its boxes will use their book default style.')) {
      return;
    }

    try {
      await axios.delete(`/api/design-presets/${presetId}`, {
        withCredentials: true,
      });
      loadPresets();
    } catch (err) {
      alert('Failed to delete text style');
      console.error(err);
    }
  };

  const handleDuplicate = async (preset) => {
    const newName = prompt('Name for the duplicated text style:', `${preset.name} (Copy)`);
    if (!newName) return;

    try {
      await axios.post(
        `/api/design-presets/${preset._id}/duplicate`,
        { name: newName },
        { withCredentials: true }
      );
      loadPresets();
    } catch (err) {
      alert('Failed to duplicate text style');
      console.error(err);
    }
  };

  const handleSave = async (presetData) => {
    try {
      if (selectedPreset) {
        // Update existing
        await axios.put(`/api/design-presets/${selectedPreset._id}`, presetData, {
          withCredentials: true,
        });
      } else {
        // Create new
        await axios.post('/api/design-presets', presetData, {
          withCredentials: true,
        });
      }
      setShowEditor(false);
      setSelectedPreset(null);
      loadPresets();
    } catch (err) {
      alert('Failed to save text style');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <BackButton />
      </div>

      <h1 className="text-2xl font-bold mb-2">Text styles</h1>
      <Link to="/layout-templates" className="mb-3 block text-sm text-blue-700">Layout templates →</Link>
      <p className="text-sm text-gray-600 mb-6">
        Text styles are shared. Changes affect boxes that use them; page layouts remain private.
      </p>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Preset List */}
        <div className="space-y-3">
          <button
            onClick={handleCreate}
            className="w-full bg-gray-900 text-white p-3 rounded hover:bg-gray-800 flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            New text style
          </button>

          {presets.length === 0 ? (
            <div className="text-gray-500 text-center p-4">
              No text styles yet. Create one to reuse its formatting.
            </div>
          ) : (
            presets.map((preset) => (
              <PresetCard
                key={preset._id}
                preset={preset}
                onEdit={() => handleEdit(preset)}
                onDelete={() => handleDelete(preset._id)}
                onDuplicate={() => handleDuplicate(preset)}
              />
            ))
          )}
        </div>

        {/* Preset Editor */}
        <div className="lg:col-span-2">
          {showEditor ? (
            <PresetEditor
              key={selectedPreset?._id || 'new'}
              preset={selectedPreset}
              onSave={handleSave}
              onCancel={() => {
                setShowEditor(false);
                setSelectedPreset(null);
              }}
            />
          ) : (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500">
              Select a text style to edit or create a new one
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PresetCard({ preset, onEdit, onDelete, onDuplicate }) {
  return (
    <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-semibold flex items-center gap-2">
            {preset.name}
            {preset.is_default && (
              <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded">
                Default
              </span>
            )}
          </h3>
          {preset.description && (
            <p className="text-sm text-gray-600 mt-1">{preset.description}</p>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-3">
        <button
          onClick={onEdit}
          className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
        >
          <Edit size={14} />
          Edit
        </button>
        <button
          onClick={onDuplicate}
          className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
        >
          <Copy size={14} />
          Duplicate
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
        >
          <Trash2 size={14} />
          Delete
        </button>
      </div>
    </div>
  );
}

PresetCard.propTypes = {
  preset: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    is_default: PropTypes.bool,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
};

function PresetEditor({ preset, onSave, onCancel }) {
  const [name, setName] = useState(preset?.name || '');
  const [description, setDescription] = useState(preset?.description || '');
  const [isDefault, setIsDefault] = useState(preset?.is_default || false);
  const [config, setConfig] = useState(
    preset?.config || {
      // All values are full-resolution (v2). Migration 005 converted all older presets.
      textWidthMode: 'fixed',
      textWidth: 833,         // 250 * 3.333
      textWidthPercentage: 75,
      maxWidth: 1333,         // 400 * 3.333
      fontSizeMode: 'adaptive',
      fontSize: 60,           // 18 * 3.333
      fontSizeRules: [
        { maxChars: 60,    size: 60 },  // 18 * 3.333
        { maxChars: 120,   size: 53 },  // 16 * 3.333
        { maxChars: 200,   size: 47 },  // 14 * 3.333
        { maxChars: 99999, size: 40 },  // 12 * 3.333
      ],
      fontFamily: 'Futura',
      fontFamilySecondary: 'Futura',
      fontStyle: 'normal',
      fontStyleSecondary: 'italic',
      lineHeight: 2,
      padding: { left: 33, right: 33, top: 33, bottom: 33 },  // 10 * 3.333
      separatorHeight: 10,    // 3 * 3.333
      separatorPadding: 20,   // 6 * 3.333
      showDivider: false,
      separatorSpacing: 0,
      allowOverflow: false,
      warnOnOverflow: true,
      autoScale: true,
      minFontSize: 33,        // 10 * 3.333
      alignment: 'center',
      backgroundMode: 'text',
    }
  );

  const updateConfig = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const updateFontSizeRule = (index, field, value) => {
    const newRules = [...config.fontSizeRules];
    newRules[index] = { ...newRules[index], [field]: Number(value) };
    updateConfig('fontSizeRules', newRules);
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      alert('Please enter a name for the text style');
      return;
    }

    onSave({
      name,
      description,
      is_default: isDefault,
      config: pick(config),
      version: 2,
    });
  };

  return (
    <div className="bg-white border rounded-lg p-6 max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-bold mb-6">
        {preset ? `Edit ${preset.name}` : 'New text style'}
      </h2>

      {/* Basic Info */}
      <section className="mb-6">
        <label className="block text-sm font-semibold mb-2">Preset Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border rounded px-3 py-2"
          placeholder="e.g., Design Happiness"
        />
      </section>

      <section className="mb-6">
        <label className="block text-sm font-semibold mb-2">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full border rounded px-3 py-2"
          rows="2"
          placeholder="Optional description"
        />
      </section>

      <section className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm font-semibold">Mark as catalog default</span>
        </label>
      </section>

      {/* Font Size Settings */}
      <section className="mb-6 pb-6 border-b">
        <h3 className="font-semibold mb-3">Font Size</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="fontSizeMode"
              value="fixed"
              checked={config.fontSizeMode === 'fixed'}
              onChange={(e) => updateConfig('fontSizeMode', e.target.value)}
            />
            <span>Fixed</span>
          </label>

          {config.fontSizeMode === 'fixed' && (
            <div className="ml-6">
              <input
                type="number"
                value={config.fontSize}
                onChange={(e) => updateConfig('fontSize', Number(e.target.value))}
                className="border rounded px-3 py-2 w-32"
                min="8"
                max="72"
              />
              <span className="ml-2 text-sm text-gray-600">px</span>
            </div>
          )}

          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="fontSizeMode"
              value="adaptive"
              checked={config.fontSizeMode === 'adaptive'}
              onChange={(e) => updateConfig('fontSizeMode', e.target.value)}
            />
            <span>Adaptive (auto-scale based on text length)</span>
          </label>

          {config.fontSizeMode === 'adaptive' && (
            <div className="ml-6 space-y-3 bg-gray-50 p-3 rounded">
              <p className="text-sm text-gray-600 mb-2">
                Font size will be automatically selected based on total text length:
              </p>
              {config.fontSizeRules.map((rule, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm w-32">
                    {index === 0
                      ? `< ${rule.maxChars} chars`
                      : index === config.fontSizeRules.length - 1
                      ? `≥ ${config.fontSizeRules[index - 1].maxChars} chars`
                      : `${config.fontSizeRules[index - 1].maxChars}-${rule.maxChars} chars`}
                    :
                  </span>
                  <input
                    type="number"
                    value={rule.size}
                    onChange={(e) => updateFontSizeRule(index, 'size', e.target.value)}
                    className="border rounded px-3 py-2 w-20"
                    min="8"
                    max="72"
                  />
                  <span className="text-sm text-gray-600">px</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Typography */}
      <section className="mb-6 pb-6 border-b">
        <h3 className="font-semibold mb-3">Typography</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Primary Font Family</label>
            <input
              type="text"
              value={config.fontFamily}
              onChange={(e) => updateConfig('fontFamily', e.target.value)}
              className="border rounded px-3 py-2 w-full"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Secondary Font Family (for bilingual)</label>
            <input
              type="text"
              value={config.fontFamilySecondary}
              onChange={(e) => updateConfig('fontFamilySecondary', e.target.value)}
              className="border rounded px-3 py-2 w-full"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Line Height</label>
            <input
              type="number"
              step="0.1"
              value={config.lineHeight}
              onChange={(e) => updateConfig('lineHeight', Number(e.target.value))}
              className="border rounded px-3 py-2 w-32"
              min="1"
              max="3"
            />
          </div>
        </div>
      </section>

      {/* Spacing */}
      <section className="mb-6 pb-6 border-b">
        <h3 className="font-semibold mb-3">Spacing</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm mb-1">Padding Left</label>
            <input
              type="number"
              value={config.padding.left}
              onChange={(e) =>
                updateConfig('padding', { ...config.padding, left: Number(e.target.value) })
              }
              className="border rounded px-3 py-2 w-full"
              min="0"
              max="50"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Padding Right</label>
            <input
              type="number"
              value={config.padding.right}
              onChange={(e) =>
                updateConfig('padding', {
                  ...config.padding,
                  right: Number(e.target.value),
                })
              }
              className="border rounded px-3 py-2 w-full"
              min="0"
              max="50"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Padding Top</label>
            <input
              type="number"
              value={config.padding.top}
              onChange={(e) =>
                updateConfig('padding', { ...config.padding, top: Number(e.target.value) })
              }
              className="border rounded px-3 py-2 w-full"
              min="0"
              max="50"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Padding Bottom</label>
            <input
              type="number"
              value={config.padding.bottom}
              onChange={(e) =>
                updateConfig('padding', {
                  ...config.padding,
                  bottom: Number(e.target.value),
                })
              }
              className="border rounded px-3 py-2 w-full"
              min="0"
              max="50"
            />
          </div>
        </div>
      </section>

      {/* Divider */}
      <section className="mb-6 pb-6 border-b">
        <h3 className="font-semibold mb-3">Bilingual Divider Line</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.showDivider || false}
              onChange={(e) => updateConfig('showDivider', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Show horizontal line between languages</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm">Space between languages</span>
            <select
              value={typeof config.separatorSpacing === 'number' ? config.separatorSpacing : 0}
              onChange={(e) => updateConfig('separatorSpacing', Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value={0}>None</option>
              <option value={1}>1×</option>
              <option value={2}>2×</option>
              <option value={3}>3×</option>
              <option value={4}>4×</option>
              <option value={5}>5×</option>
            </select>
          </div>
        </div>
      </section>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-6 border-t">
        <button
          onClick={handleSubmit}
          className="flex items-center gap-2 bg-gray-900 text-white px-6 py-2 rounded hover:bg-gray-800"
        >
          <Check size={16} />
          Save Preset
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-2 border border-gray-300 px-6 py-2 rounded hover:bg-gray-50"
        >
          <X size={16} />
          Cancel
        </button>
      </div>
    </div>
  );
}

PresetEditor.propTypes = {
  preset: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    is_default: PropTypes.bool,
    config: PropTypes.object,
    version: PropTypes.number,
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
